// The document: catalog, page tree, page boxes, outlines, page labels (ISO 32000-1 §7.7, §12.3).

import { makeCipher } from "./crypto";
import { Dict, PName, PRef, Stream, XRef, type PObj } from "./parse";

export type Box = [number, number, number, number];

export interface OutlineItem { title: string; dest: Dest | null; children: OutlineItem[]; open: boolean }
export interface Dest { page: number; x: number | null; y: number | null; zoom: number | null }

const num = (o: PObj): number | null => (typeof o === "number" ? o : null);

export class PdfPage {
    private _content: Uint8Array | null = null;
    constructor(public readonly doc: PdfDocument, public readonly index: number, public readonly dict: Dict, private readonly inherited: Dict) {}

    private inh(key: string): PObj {
        const v = this.dict.get(key);
        return v !== null ? v : this.inherited.get(key);
    }
    get resources(): Dict { const r = this.inh("Resources"); return r instanceof Dict ? r : new Dict(this.doc.xref); }
    private box(key: string): Box | null {
        const a = this.inh(key);
        if (!Array.isArray(a) || a.length < 4) return null;
        const n = a.slice(0, 4).map((x) => num(this.doc.xref.resolve(x)));
        if (n.some((x) => x === null)) return null;
        const [x0, y0, x1, y1] = n as number[];
        return [Math.min(x0!, x1!), Math.min(y0!, y1!), Math.max(x0!, x1!), Math.max(y0!, y1!)];
    }
    get mediaBox(): Box { return this.box("MediaBox") ?? [0, 0, 612, 792]; }
    get cropBox(): Box {
        const m = this.mediaBox, c = this.box("CropBox");
        if (!c) return m;
        const r: Box = [Math.max(m[0], c[0]), Math.max(m[1], c[1]), Math.min(m[2], c[2]), Math.min(m[3], c[3])];
        return r[2] > r[0] && r[3] > r[1] ? r : m;
    }
    /** Clockwise rotation in degrees: 0, 90, 180 or 270. */
    get rotate(): number { const r = this.inh("Rotate"); return typeof r === "number" ? ((((r % 360) + 360) % 360 / 90 | 0) * 90) : 0; }
    /** The page's size in points as displayed (rotation applied). */
    get size(): [number, number] {
        const b = this.cropBox;
        const w = b[2] - b[0], h = b[3] - b[1];
        return this.rotate % 180 === 90 ? [h, w] : [w, h];
    }
    /** The base transform from default user space to a top-left-origin viewport of `scale` px per point. */
    viewMatrix(scale: number, rotation = 0): [number, number, number, number, number, number] {
        const b = this.cropBox;
        const rot = (this.rotate + rotation + 360) % 360;
        const w = b[2] - b[0], h = b[3] - b[1];
        switch (rot) {
            case 90: return [0, scale, scale, 0, -b[1] * scale, -b[0] * scale];
            case 180: return [-scale, 0, 0, scale, b[2] * scale, -b[1] * scale];
            case 270: return [0, -scale, -scale, 0, b[3] * scale, b[2] * scale];
            default: void w; void h; return [scale, 0, 0, -scale, -b[0] * scale, b[3] * scale];
        }
    }
    /** The page's content stream: all of /Contents joined. */
    content(): Uint8Array {
        if (this._content) return this._content;
        const c = this.dict.get("Contents");
        const parts: Uint8Array[] = [];
        const add = (o: PObj) => { const v = this.doc.xref.resolve(o); if (v instanceof Stream) parts.push(v.bytes()); else if (Array.isArray(v)) v.forEach(add); };
        add(c);
        const total = parts.reduce((s, p) => s + p.length + 1, 0);
        const out = new Uint8Array(total);
        let p = 0;
        for (const x of parts) { out.set(x, p); p += x.length; out[p++] = 10; }
        return (this._content = out);
    }
    /** The annotation dictionaries of the page. */
    annots(): Dict[] {
        const a = this.dict.get("Annots");
        if (!Array.isArray(a)) return [];
        const out: Dict[] = [];
        for (const x of a) { const d = this.doc.xref.resolve(x); if (d instanceof Dict) out.push(d); }
        return out;
    }
    get ref(): PRef | null { return this.doc.pageRefs[this.index] ?? null; }

    /** The link annotations: where they are (user space), the URI or the destination page they lead to. */
    links(): Array<{ rect: Box; uri: string | null; page: number | null; x: number | null; y: number | null }> {
        const out: Array<{ rect: Box; uri: string | null; page: number | null; x: number | null; y: number | null }> = [];
        for (const a of this.annots()) {
            if (a.name("Subtype") !== "Link") continue;
            const r = (a.arr("Rect") ?? []).map((x) => { const v = this.doc.xref.resolve(x); return typeof v === "number" ? v : 0; });
            if (r.length < 4) continue;
            const rect: Box = [Math.min(r[0]!, r[2]!), Math.min(r[1]!, r[3]!), Math.max(r[0]!, r[2]!), Math.max(r[1]!, r[3]!)];
            const act = a.dict("A");
            let uri: string | null = null, dest: Dest | null = null;
            if (act) {
                const s = act.name("S");
                if (s === "URI") uri = textString(act.get("URI"));
                else if (s === "GoTo") dest = this.doc.resolveDest(act.get("D"));
            } else if (a.has("Dest")) dest = this.doc.resolveDest(a.get("Dest"));
            if (uri || dest) out.push({ rect, uri, page: dest?.page ?? null, x: dest?.x ?? null, y: dest?.y ?? null });
        }
        return out;
    }
}

export class PdfDocument {
    readonly xref: XRef;
    readonly catalog: Dict;
    private pageList: PdfPage[] | null = null;
    pageRefs: Array<PRef | null> = [];

    private constructor(xref: XRef, catalog: Dict) {
        this.xref = xref;
        this.catalog = catalog;
    }

    /** Opens a PDF from its bytes. Throws when it is not a PDF, or `PasswordRequired` when it needs a password (pass it as `password` to try again). */
    static open(bytes: Uint8Array, password = ""): PdfDocument {
        // find the header (it may follow junk)
        let start = -1;
        for (let i = 0; i < Math.min(bytes.length - 5, 1024); i++) if (bytes[i] === 0x25 && bytes[i + 1] === 0x50 && bytes[i + 2] === 0x44 && bytes[i + 3] === 0x46 && bytes[i + 4] === 0x2d) { start = i; break; }
        if (start < 0) throw new Error("This file is not a PDF.");
        const data = start > 0 ? bytes.subarray(start) : bytes;
        const xref = new XRef(data);
        xref.open();
        // decryption is on before any object past the trailer is used
        const cipher = makeCipher(xref, password);
        if (cipher) {
            xref.cipher = cipher;
            // objects loaded while locating the trailer were not decrypted: reload them
            (xref as unknown as { clearCache(): void }).clearCache?.();
        }
        const root = xref.trailer.get("Root");
        if (!(root instanceof Dict)) throw new Error("The PDF has no document catalog.");
        return new PdfDocument(xref, root);
    }

    get numPages(): number { return this.pages().length; }
    page(i: number): PdfPage { const p = this.pages()[i]; if (!p) throw new RangeError("no such page"); return p; }

    pages(): PdfPage[] {
        if (this.pageList) return this.pageList;
        const out: PdfPage[] = [];
        const refs: Array<PRef | null> = [];
        const seen = new Set<Dict>();
        const walk = (node: PObj, ref: PRef | null, inherited: Dict) => {
            if (!(node instanceof Dict) || seen.has(node)) return;
            seen.add(node);
            const kids = node.get("Kids");
            if (Array.isArray(kids) && node.name("Type") !== "Page") {
                const inh = new Dict(this.xref);
                for (const k of ["Resources", "MediaBox", "CropBox", "Rotate"]) { const v = node.raw(k); if (v !== undefined) inh.set(k, v); else { const pv = inherited.raw(k); if (pv !== undefined) inh.set(k, pv); } }
                for (const k of kids) { walk(this.xref.resolve(k), k instanceof PRef ? k : null, inh); }
            } else {
                out.push(new PdfPage(this, out.length, node, inherited));
                refs.push(ref);
            }
        };
        walk(this.catalog.get("Pages"), this.catalog.raw("Pages") instanceof PRef ? (this.catalog.raw("Pages") as PRef) : null, new Dict(this.xref));
        if (!out.length) {
            // a broken tree: take every /Type /Page object
            for (const [n, e] of this.xref.entries) {
                if (e.type === 0) continue;
                const o = this.xref.fetch(new PRef(n, e.type === 1 ? e.b : 0));
                if (o instanceof Dict && o.name("Type") === "Page") { out.push(new PdfPage(this, out.length, o, new Dict(this.xref))); refs.push(new PRef(n, e.type === 1 ? e.b : 0)); }
            }
        }
        this.pageRefs = refs;
        return (this.pageList = out);
    }

    info(): { title: string; author: string; subject: string; producer: string } {
        const d = this.xref.trailer.get("Info");
        const s = (k: string) => (d instanceof Dict ? textString(d.get(k)) : "");
        return { title: s("Title"), author: s("Author"), subject: s("Subject"), producer: s("Producer") };
    }

    /** Resolves a destination (an array, a name, or an action's /D) to a page and position. */
    resolveDest(d: PObj): Dest | null {
        let v = this.xref.resolve(d);
        if (typeof v === "string" || v instanceof PName) {
            const key = typeof v === "string" ? v : v.name;
            v = this.namedDest(key);
        }
        if (v instanceof Dict) v = v.get("D");
        if (!Array.isArray(v) || !v.length) return null;
        const p = v[0];
        let page = -1;
        if (typeof p === "number") page = p;
        else if (p instanceof PRef) {
            this.pages();
            page = this.pageRefs.findIndex((r) => r && r.num === p.num);
        }
        if (page < 0) return null;
        const kind = v[1] instanceof PName ? v[1].name : "";
        const n = (i: number) => { const x = this.xref.resolve(v[i]); return typeof x === "number" ? x : null; };
        if (kind === "XYZ") return { page, x: n(2), y: n(3), zoom: n(4) };
        if (kind === "FitH" || kind === "FitBH") return { page, x: null, y: n(2), zoom: null };
        return { page, x: null, y: null, zoom: null };
    }

    private namedDest(name: string): PObj {
        const dests = this.catalog.get("Dests");
        if (dests instanceof Dict) { const v = dests.get(name); if (v) return v; }
        const names = this.catalog.dict("Names")?.dict("Dests");
        const find = (node: Dict | null): PObj => {
            if (!node) return null;
            const arr = node.arr("Names");
            if (arr) for (let i = 0; i + 1 < arr.length; i += 2) { const k = this.xref.resolve(arr[i]); if (k === name) return this.xref.resolve(arr[i + 1]); }
            const kids = node.arr("Kids");
            if (kids) for (const k of kids) { const kd = this.xref.resolve(k); if (kd instanceof Dict) { const lim = kd.arr("Limits"); if (lim && typeof lim[0] === "string" && typeof lim[1] === "string" && (name < lim[0] || name > lim[1])) continue; const r = find(kd); if (r) return r; } }
            return null;
        };
        return find(names ?? null);
    }

    outline(): OutlineItem[] {
        const root = this.catalog.dict("Outlines");
        if (!root) return [];
        const seen = new Set<Dict>();
        const items = (first: PObj, depth: number): OutlineItem[] => {
            const out: OutlineItem[] = [];
            let cur = this.xref.resolve(first);
            while (cur instanceof Dict && !seen.has(cur) && out.length < 5000) {
                seen.add(cur);
                let dest: Dest | null = null;
                const action = cur.dict("A");
                if (cur.has("Dest")) dest = this.resolveDest(cur.get("Dest"));
                else if (action && action.name("S") === "GoTo") dest = this.resolveDest(action.get("D"));
                out.push({ title: textString(cur.get("Title")), dest, children: depth < 12 ? items(cur.raw("First") ?? null, depth + 1) : [], open: cur.num("Count") > 0 });
                cur = this.xref.resolve(cur.raw("Next") ?? null);
            }
            return out;
        };
        return items(root.raw("First") ?? null, 0);
    }

    /** The label of each page ("iv", "A-3" …); the page number when there are no labels. */
    pageLabels(): string[] {
        const n = this.numPages;
        const tree = this.catalog.dict("PageLabels");
        const ranges: Array<[number, Dict]> = [];
        const walk = (node: Dict | null) => {
            if (!node) return;
            const nums = node.arr("Nums");
            if (nums) for (let i = 0; i + 1 < nums.length; i += 2) { const k = this.xref.resolve(nums[i]); const v = this.xref.resolve(nums[i + 1]); if (typeof k === "number" && v instanceof Dict) ranges.push([k, v]); }
            const kids = node.arr("Kids");
            if (kids) for (const k of kids) { const d = this.xref.resolve(k); if (d instanceof Dict) walk(d); }
        };
        walk(tree);
        ranges.sort((a, b) => a[0] - b[0]);
        const labels: string[] = [];
        for (let i = 0; i < n; i++) {
            let r: [number, Dict] | null = null;
            for (const x of ranges) if (x[0] <= i) r = x; else break;
            if (!r) { labels.push(String(i + 1)); continue; }
            const [start, d] = r;
            const v = d.num("St", 1) + (i - start);
            const style = d.name("S");
            const prefix = textString(d.get("P"));
            labels.push(prefix + (style === "D" ? String(v) : style === "R" ? roman(v).toUpperCase() : style === "r" ? roman(v) : style === "A" ? alpha(v).toUpperCase() : style === "a" ? alpha(v) : ""));
        }
        return labels;
    }

    /** Releases nothing: kept so callers can drop the document. */
    close() { this.pageList = null; }
}

function roman(n: number): string {
    const t: Array<[number, string]> = [[1000, "m"], [900, "cm"], [500, "d"], [400, "cd"], [100, "c"], [90, "xc"], [50, "l"], [40, "xl"], [10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"]];
    let r = "";
    for (const [v, s] of t) while (n >= v) { r += s; n -= v; }
    return r;
}
function alpha(n: number): string { let r = ""; const k = Math.floor((n - 1) / 26) + 1; for (let i = 0; i < k; i++) r += String.fromCharCode(97 + ((n - 1) % 26)); return r; }

/** A PDF text string (PDFDocEncoding or UTF-16 / UTF-8 with a byte order mark) as a JS string. */
export function textString(o: PObj): string {
    if (typeof o !== "string") return "";
    if (o.charCodeAt(0) === 0xfe && o.charCodeAt(1) === 0xff) { let r = ""; for (let i = 2; i + 1 < o.length; i += 2) r += String.fromCharCode((o.charCodeAt(i) << 8) | o.charCodeAt(i + 1)); return r; }
    if (o.charCodeAt(0) === 0xff && o.charCodeAt(1) === 0xfe) { let r = ""; for (let i = 2; i + 1 < o.length; i += 2) r += String.fromCharCode((o.charCodeAt(i + 1) << 8) | o.charCodeAt(i)); return r; }
    if (o.charCodeAt(0) === 0xef && o.charCodeAt(1) === 0xbb && o.charCodeAt(2) === 0xbf) { try { return new TextDecoder("utf-8").decode(Uint8Array.from(o.slice(3), (c) => c.charCodeAt(0))); } catch { return o.slice(3); } }
    return o;
}
